import { useEffect, useState } from "react";
import { Bluetooth, BluetoothConnected, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  isBleSupported, getBleState, connectReader, disconnectReader, toggleScan,
  getTxPower, setTxPower,
} from "@/lib/bleReader";

const POWER_OPTIONS = [10, 15, 20, 25, 30];

/**
 * Compact header button to pair the R2 Bluetooth UHF reader.
 * Grey = not connected (tap to pair) · Blue = connected (tap to disconnect).
 * Shows a live counter while a scan burst is in flight.
 */
export const BleReaderButton = ({ light = false }) => {
  const [st, setSt] = useState(getBleState());

  useEffect(() => {
    const onStatus = (e) => setSt({ ...getBleState(), ...e.detail });
    window.addEventListener("ble-reader-status", onStatus);
    return () => window.removeEventListener("ble-reader-status", onStatus);
  }, []);

  const native = typeof window !== "undefined" && window.NativeBle;

  if (!native && !isBleSupported()) {
    return (
      <button
        onClick={() => toast.error("This browser doesn't support Bluetooth. Use Chrome or Edge on Android/PC (iPhone Safari is not supported).")}
        data-testid="ble-reader-button"
        title="Bluetooth not supported in this browser"
        className={`h-11 px-2.5 flex items-center rounded-md opacity-40 ${light ? "text-neutral-400 border border-neutral-300" : "text-neutral-500"}`}
      >
        <Bluetooth className="h-5 w-5" />
      </button>
    );
  }

  const onClick = async () => {
    if (native) {
      // Inside the Android app: delegate to the native BLE bridge (WebView
      // has no Web Bluetooth). Status/EPC events come back via the same
      // window events, so the rest of the UI works unchanged.
      if (st.connected) { native.disconnect(); toast.info("Scanner disconnected"); }
      else { native.connect(); }
      return;
    }
    if (st.connecting) return;
    if (st.connected) {
      disconnectReader();
      toast.info("Scanner disconnected");
      return;
    }
    try {
      const name = await connectReader();
      toast.success(`Scanner connected: ${name} — pull the trigger to scan`);
    } catch (e) {
      if (!/cancell?ed/i.test(e?.message || "")) toast.error(e?.message || "Bluetooth connection failed");
    }
  };

  return (
    <span className="flex items-center gap-1.5">
      {st.connected && !native && (
        <select
          value={getTxPower()}
          onChange={(e) => { setTxPower(Number(e.target.value)); toast.success(`Power: ${e.target.value} dBm`); setSt({ ...getBleState() }); }}
          data-testid="ble-power-select"
          title="Scanner TX power (higher = longer range)"
          className={`h-11 px-1.5 rounded-md text-sm font-semibold border outline-none ${
            light ? "bg-white text-neutral-700 border-neutral-300" : "bg-neutral-800 text-neutral-200 border-neutral-600"
          }`}
        >
          {POWER_OPTIONS.map((p) => (
            <option key={p} value={p}>{p} dBm</option>
          ))}
        </select>
      )}
      {st.connected && (
        <button
          onClick={() => (native ? native.toggleScan() : toggleScan())}
          data-testid="ble-scan-toggle"
          className={`h-11 px-4 rounded-md text-sm font-bold tracking-wide transition-colors ${
            st.scanning
              ? "bg-red-600 text-white hover:bg-red-700 animate-pulse"
              : "bg-emerald-600 text-white hover:bg-emerald-700"
          }`}
        >
          {st.scanning ? `STOP${st.pending > 0 ? ` · ${st.pending}` : ""}` : "SCAN"}
        </button>
      )}
      <button
        onClick={onClick}
        data-testid="ble-reader-button"
        title={st.connected ? `Connected: ${st.name || "reader"} — tap to disconnect` : "Connect Bluetooth RFID scanner"}
        className={`h-11 px-2.5 flex items-center gap-1.5 rounded-md text-sm font-semibold transition-colors ${
          st.connected
            ? "bg-sky-600 text-white hover:bg-sky-700"
            : light
              ? "text-neutral-500 border border-neutral-300 hover:bg-neutral-100"
              : "text-neutral-300 hover:bg-neutral-800 active:bg-neutral-700"
        }`}
      >
        {st.connecting ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : st.connected ? (
          <BluetoothConnected className="h-5 w-5" />
        ) : (
          <Bluetooth className="h-5 w-5" />
        )}
        {st.connected && st.sessionTotal > 0 && (
          <span data-testid="ble-reader-count" className="text-xs font-bold">{st.sessionTotal}</span>
        )}
      </button>
    </span>
  );
};
